import { objectSpread2 as _objectSpread2, extends as _extends, toConsumableArray as _toConsumableArray, defineProperty as _defineProperty } from '../../../_virtual/_rollupPluginBabelHelpers.esm.js';
import { textArrayToDelimitedString } from '@costar-ui/utils';
import { useFormValidation } from '@react-aria/form';
import { useInteractionModality } from '@react-aria/interactions';
import { useFormReset } from '@react-aria/utils';
import { useVisuallyHidden } from '@react-aria/visually-hidden';
import React, { useRef } from 'react';
import { selectData } from './use-select.esm.js';

/**
 * Provides the behavior and accessibility implementation for a hidden `<select>` element, which
 * can be used in combination with `useSelect` to support browser form autofill, mobile form
 * navigation, and native HTML form submission.
 */
function useHiddenSelect(props, state, triggerRef) {
  var _state$selectedKey;
  var data = selectData.get(state) || {};
  var autoComplete = props.autoComplete,
    _props$name = props.name,
    name = _props$name === void 0 ? data.name : _props$name,
    _props$isDisabled = props.isDisabled,
    isDisabled = _props$isDisabled === void 0 ? data.isDisabled : _props$isDisabled;
  var validationBehavior = data.validationBehavior,
    isRequired = data.isRequired;
  var modality = useInteractionModality();
  var _useVisuallyHidden = useVisuallyHidden(),
    visuallyHiddenProps = _useVisuallyHidden.visuallyHiddenProps;

  // @ts-ignore
  useFormReset(props.selectRef, state.selectedKey, state.setSelectedKey);
  useFormValidation({
    validationBehavior: validationBehavior,
    focus: function focus() {
      return triggerRef.current && triggerRef.current.focus();
    }
  }, state, props.selectRef);

  // In Safari, the <select> cannot have `display: none` or `hidden` for autofill to work.
  // In Firefox, there must be a <label> to identify the <select> whereas other browsers
  // seem to identify it just by surrounding text.
  // The solution is to use <VisuallyHidden> to hide the elements, which clips the elements to a
  // 1px rectangle. In addition, we hide from screen readers with aria-hidden, and make the <select>
  // non tabbable with tabIndex={-1}.
  //
  // In mobile browsers, there are next/previous buttons above the software keyboard for navigating
  // between fields in a form. These only support native form inputs that are tabbable. In order to
  // support those, an additional hidden input is used to marshall focus to the button. It is tabbable
  // except when the button is focused, so that shift tab works properly to go to the actual previous
  // input in the form. Using the <select> for this also works, but Safari on iOS briefly flashes
  // the native menu on focus, so this isn't ideal. A font-size of 16px or greater is required to
  // prevent Safari from zooming in on the input when it is focused.
  //
  // If the current interaction modality is null, then the user hasn't interacted with the page yet.
  // In this case, we set the tabIndex to -1 on the input element so that automated accessibility
  // checkers don't throw false-positives about focusable elements inside an aria-hidden parent.
  return {
    containerProps: _objectSpread2(_objectSpread2({}, visuallyHiddenProps), {}, _defineProperty(_defineProperty({
      'aria-hidden': true
    }, 'data-react-aria-prevent-focus', true), 'data-a11y-ignore', 'aria-hidden-focus')),
    inputProps: {
      type: 'text',
      tabIndex: modality == null || state.isFocused || state.isOpen ? -1 : 0,
      style: {
        fontSize: 16
      },
      onFocus: function onFocus() {
        return triggerRef.current && triggerRef.current.focus();
      },
      disabled: isDisabled
    },
    selectProps: {
      tabIndex: -1,
      autoComplete: autoComplete,
      disabled: isDisabled,
      required: validationBehavior === 'native' && isRequired,
      name: name,
      value: (_state$selectedKey = state.selectedKey) !== null && _state$selectedKey !== void 0 ? _state$selectedKey : '',
      onChange: function onChange(e) {
        return state.setSelectedKey(e.target.value);
      }
    }
  };
}

/**
 * Renders a hidden native `<select>` element, which can be used to support browser
 * form autofill, mobile form navigation, and native form submission.
 */
function HiddenSelect(props) {
  var state = props.state,
    triggerRef = props.triggerRef,
    label = props.label,
    name = props.name,
    isDisabled = props.isDisabled;
  var selectRef = useRef(null);
  var _useHiddenSelect = useHiddenSelect(_objectSpread2(_objectSpread2({}, props), {}, {
      selectRef: selectRef
    }), state, triggerRef),
    containerProps = _useHiddenSelect.containerProps,
    inputProps = _useHiddenSelect.inputProps,
    selectProps = _useHiddenSelect.selectProps;
  var value;
  if (Array.isArray(state.selectedKey)) {
    value = textArrayToDelimitedString(state.selectedKey);
  } else {
    var _ref;
    value = (_ref = "".concat(state.selectedKey)) !== null && _ref !== void 0 ? _ref : '';
  }

  // If used in a <form>, use a hidden input so the value can be submitted to a server.
  // If the collection isn't too big, use a hidden <select> element for this so that browser
  // autofill will work. Otherwise, use an <input type="hidden">.
  if (state.collection.size <= 300) {
    return /*#__PURE__*/React.createElement("div", _extends({}, containerProps, {
      "data-testid": "hidden-select-container"
    }), /*#__PURE__*/React.createElement("input", inputProps), /*#__PURE__*/React.createElement("label", null, label, /*#__PURE__*/React.createElement("select", _extends({}, selectProps, {
      value: value,
      ref: selectRef
    }), /*#__PURE__*/React.createElement("option", null), _toConsumableArray(state.collection.getKeys()).map(function (key) {
      var item = state.collection.getItem(key);
      if (item && item.type === 'item') {
        return /*#__PURE__*/React.createElement("option", {
          key: item.key,
          value: item.key
        }, item.textValue);
      }
      return null;
    }))));
  } else if (name) {
    return /*#__PURE__*/React.createElement("input", {
      type: "hidden",
      autoComplete: selectProps.autoComplete,
      name: name,
      disabled: isDisabled,
      value: value
    });
  }
  return null;
}

export { HiddenSelect, useHiddenSelect };
//# sourceMappingURL=hidden-select.esm.js.map
